import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Sparkles, X, XCircle } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOrgModules } from '../../../hooks/useOrgModules';
import { useAnalysisRunsPolling, useAnalysisRunsStore } from '../../../stores/analysisRunsStore';
import { isRunActive, type AnalysisRun } from '../../../types/projectAnalysis';
import { phaseLabel } from './analysisFormat';

const analysisPath = (projectId: string) => `/projets/${projectId}/analyse`;

/**
 * Bottom-right dock listing the project analyses in flight (whoever started
 * them) and the ones just finished, on every page. Also drives the polling.
 * Mounted once in AppLayout; renders nothing when idle.
 */
export const AnalysisRunsDock: React.FC = () => {
  const { moduleEnabled } = useOrgModules();
  const { canRead } = usePermissions();
  const enabled = moduleEnabled('projects') && canRead('projects');
  useAnalysisRunsPolling(enabled);

  const { t } = useTranslation();
  const runs = useAnalysisRunsStore((s) => s.runs);
  const dismissed = useAnalysisRunsStore((s) => s.dismissed);
  const dismiss = useAnalysisRunsStore((s) => s.dismiss);

  const visible = runs.filter((r) => !dismissed[r.id]);
  const anyActive = visible.some(isRunActive);
  const now = useNow(anyActive);

  if (!enabled || visible.length === 0) return null;

  return (
    <aside
      className="fixed bottom-4 right-4 z-40 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-card shadow-lg"
      aria-label={t('projects.runs.dockLabel')}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Sparkles className="h-4 w-4 text-primary" aria-hidden />
        <span className="text-xs font-semibold text-foreground">{t('projects.runs.dockTitle')}</span>
        {!anyActive && (
          <button
            type="button"
            onClick={() => visible.forEach((r) => dismiss(r.id))}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
          >
            {t('projects.runs.dismissAll')}
          </button>
        )}
      </div>
      <ul className="max-h-64 divide-y divide-border overflow-y-auto" role="status" aria-live="polite">
        {visible.map((run) => (
          <RunRow key={run.id} run={run} now={now} onDismiss={() => dismiss(run.id)} />
        ))}
      </ul>
    </aside>
  );
};

const RunRow: React.FC<{ run: AnalysisRun; now: number; onDismiss: () => void }> = ({ run, now, onDismiss }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = isRunActive(run);
  const title = run.project_title || t('projects.runs.untitled');
  const to = analysisPath(run.project_id);

  const started = Date.parse(run.started_at ?? run.created_at);
  const secs = Number.isNaN(started) ? 0 : Math.max(0, Math.floor((now - started) / 1000));
  const elapsed = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  return (
    <li className="flex items-start gap-2.5 px-3 py-2.5 text-sm">
      <span className="mt-0.5 shrink-0">
        {active ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
        ) : run.status === 'succeeded' ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
        ) : (
          <XCircle className="h-4 w-4 text-destructive" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground" title={title}>
          {title}
        </p>
        <p
          className={cn(
            'mt-0.5 text-xs',
            run.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {active
            ? phaseLabel(run, t)
            : run.status === 'succeeded'
              ? t('projects.runs.succeeded')
              : run.error || t('projects.runs.failed')}
        </p>
        {!active && run.status === 'succeeded' && pathname !== to && (
          <Link to={to} onClick={onDismiss} className="mt-1 inline-block text-xs font-medium text-primary hover:underline">
            {t('projects.runs.seeAnalysis')}
          </Link>
        )}
        {active && pathname !== to && (
          <Link to={to} className="mt-1 inline-block text-xs font-medium text-primary hover:underline">
            {t('projects.runs.follow')}
          </Link>
        )}
      </div>
      {active ? (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{elapsed}</span>
      ) : (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('projects.runs.dismiss')}
          title={t('projects.runs.dismiss')}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
};

/** Current time, refreshed every second while `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

export default AnalysisRunsDock;
